import React, { useMemo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import CollapsibleBoxComponent from '../../../components/CollapsibleBox/CollapsibleBoxComponent';
import PillOptionSelectorComponent from '../../../components/PillOptionSelector/PillOptionSelectorComponent';
import RadioPillSelectorComponent from '../../../components/RadioPillSelector/RadioPillSelectorComponent';
import LoadingDots from '../../../components/LoadingDots/LoadingDots';
import commsTemplate from '../../../json/components/subscreens/CommunicationDetailsTemplate.json';

const applyValuesToTemplate = (template, values) => {
  const merged = JSON.parse(JSON.stringify(template));
  Object.entries(values || {}).forEach(([key, value]) => {
    if (merged[key]) {
      merged[key].value = value ?? '';
    }
  });
  return merged;
};

const CommunicationPreferencesContent = ({
  handleSave,
  resetValues,
  isMobile,
  values = {},
  onFieldChange,
  isSaving = false,
  isResetting = false,
}) => {
  const merged = useMemo(
    () => applyValuesToTemplate(commsTemplate, values),
    [values]
  );

  const handleChange = (name, value) => {
    onFieldChange?.(name, value);
  };

  return (
    <CollapsibleBoxComponent
      title="Communication preferences"
      isMobile={isMobile}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ borderBottom: '1px solid #E2E5EA', pb: 2.5 }}>
          <Typography
            sx={{
              fontWeight: 400,
              lineHeight: '120%',
              fontFamily: 'Inter, sans-serif',
              fontSize: isMobile ? '0.8125rem' : '0.75rem',
              color: '#1C2F3A',
            }}
          >
            Tell us how you&rsquo;d like to keep in touch
          </Typography>
        </Box>

        {Object.entries(merged).map(([name, question]) => {
          const ControlComponent =
            question.type === 'pill-options'
              ? PillOptionSelectorComponent
              : question.type === 'radio-pill-options'
                ? RadioPillSelectorComponent
                : null;

          return (
            <Box key={name} sx={{ borderBottom: '1px solid #E2E5EA', pb: 2.5 }}>
              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        lineHeight: '120%',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '1rem',
                        color: '#1C2F3A',
                      }}
                    >
                      {question.label}
                    </Typography>
                    {question.description && (
                      <Typography
                        sx={{
                          fontWeight: 400,
                          lineHeight: '120%',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.875rem',
                          color: '#1C2F3A',
                        }}
                      >
                        {question.description}
                      </Typography>
                    )}
                  </Box>

                  {ControlComponent ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <ControlComponent
                        name={name}
                        question={question}
                        options={question.options}
                        value={values[name]}
                        onChange={handleChange}
                        multiple={question.multiple === true}
                        isMobile={isMobile}
                        layoutVariant="account"
                      />
                    </Box>
                  ) : null}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box sx={{ maxWidth: '430px', flex: '1 1 430px' }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        lineHeight: '120%',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.9375rem',
                        color: '#1C2F3A',
                      }}
                    >
                      {question.label}
                    </Typography>
                    {question.description && (
                      <Typography
                        sx={{
                          fontWeight: 400,
                          lineHeight: '120%',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.75rem',
                          color: '#1C2F3A',
                          maxWidth: '430px',
                        }}
                      >
                        {question.description}
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      flex: '0 0 auto',
                      width: 'auto',
                    }}
                  >
                    {ControlComponent ? (
                      <ControlComponent
                        name={name}
                        question={question}
                        options={question.options}
                        value={values[name]}
                        onChange={handleChange}
                        multiple={question.multiple === true}
                        isMobile={isMobile}
                        layoutVariant="account"
                      />
                    ) : null}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mt: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
          }}
        >
          <Button
            color="inherit"
            onClick={resetValues}
            disabled={isResetting}
            sx={{
              backgroundColor: '#E2E5EA',
              px: 4,
              py: 1.5,
              borderRadius: '20px',
              fontWeight: 600,
              lineHeight: '24px',
              fontFamily: 'Inter, sans-serif',
              fontSize: isMobile ? '0.8125rem' : '1.25rem',
              color: '#1C2F3A',
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: '120px' },
            }}
          >
            {isResetting ? <LoadingDots /> : 'Reset'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            sx={{
              backgroundColor: '#C7CA00',
              px: 4,
              py: 1.5,
              borderRadius: '20px',
              fontWeight: 600,
              lineHeight: '24px',
              fontFamily: 'Inter, sans-serif',
              fontSize: isMobile ? '0.8125rem' : '1.25rem',
              color: '#1C2F3A',
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: '205px' },
            }}
          >
            {isSaving ? <LoadingDots /> : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </CollapsibleBoxComponent>
  );
};

export default CommunicationPreferencesContent;
